import React, { useEffect, useRef, useState } from "react";

import { Input, message } from "antd";

import { PictureOutlined } from "@ant-design/icons";

import LdasProductCategoryPanel from "./LdasProductCategoryPanel";

const { TextArea } = Input;

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png"]);
const IMAGE_INPUT_ACCEPT = "image/jpeg,image/png,.jpg,.jpeg,.png";

const normalizeSingleCategoryTag = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) return [];
  return [tags[0]];
};

const LdasFormField = ({ label, required, children }) => (
  <div className="ldas-form-field">
    <label className="ldas-form-field__label">
      {label}
      {required && <span className="ldas-form-field__required">*</span>}
    </label>
    {children}
  </div>
);

/**
 * @param {{ onSubmit, onCancel, onDelete?, initialValues?, mode?, categories?: Array<{ id: number, name: string }>, onCategoriesInvalidated? }} props
 * mode: 'create' | 'edit'  (기본값: 'create')
 * initialValues: edit 모드에서 기존 제품 데이터
 * categories: API 카테고리 목록 배열
 * onDelete: edit 전용 — 제품 삭제(확인 모달·API는 호출부)
 */
const LdasProductForm = ({
  onSubmit,
  onCancel,
  onDelete,
  initialValues = {},
  mode = "create",
  categories = [],
  onCategoriesInvalidated,
}) => {
  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState(false);
  const [form, setForm] = useState({
    brand: initialValues.brand ?? null,
    lotNumber: initialValues.lotNumber ?? "",
    name: initialValues.name ?? "",
    description: initialValues.description ?? "",
    categoryTags: normalizeSingleCategoryTag(initialValues.categoryTags),
    image: initialValues.image ?? null,
  });
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    const { image } = form;
    if (!(image instanceof File)) {
      setFilePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(image);
    setFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.image]);

  const imagePreviewSrc = !form.image
    ? null
    : typeof form.image === "string"
    ? form.image
    : filePreviewUrl;

  const handleOpenImagePicker = () => imageInputRef.current?.click();

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      message.error("JPG 또는 PNG 이미지만 업로드할 수 있습니다");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      message.error("파일 크기는 5MB 이하여야 합니다");
      return;
    }
    setForm((prev) => ({ ...prev, image: file }));
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, image: null }));
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const isCategoryTagSelected = (cat) => {
    const t = form.categoryTags[0];
    if (t && typeof t === "object") {
      return t.id === cat.id;
    }
    if (typeof t === "string") {
      return t === cat.name;
    }
    return false;
  };

  const handleTagToggle = (cat) => {
    setForm((prev) => {
      const selected = prev.categoryTags[0];
      if (selected && typeof selected === "object" && selected.id === cat.id) {
        return { ...prev, categoryTags: [] };
      }
      if (typeof selected === "string" && selected === cat.name) {
        return { ...prev, categoryTags: [] };
      }
      return { ...prev, categoryTags: [cat] };
    });
  };

  const handleToggleCategoryPanel = () =>
    setIsCategoryPanelOpen((open) => !open);

  return (
    <div className="ldas-create-layout">
      <div className="ldas-create-form-card">
        <div className="ldas-create-form-card__body">
          {/* 브랜드 + 로트번호 */}
          {/* <div className="ldas-form-row"> */}
          <LdasFormField label="브랜드" required>
            <Input
              className="ldas-form-select"
              placeholder="브랜드 입력"
              value={form.brand}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, brand: e.target.value }))
              }
              maxLength={250}
            />
          </LdasFormField>

          {/* 제품명 */}
          <LdasFormField label="제품명" required>
            <Input
              className="ldas-form-input"
              placeholder="예: Vitamin D3 5000 IU"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={250}
            />
          </LdasFormField>

          {/* 제품 설명 */}
          <LdasFormField label="제품 설명" required>
            <TextArea
              className="ldas-form-textarea"
              placeholder="제품에 대한 설명을 입력하세요."
              rows={4}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              maxLength={250}
              showCount
            />
          </LdasFormField>

          {/* 카테고리 태그 */}
          <div className="ldas-form-section">
            <div className="ldas-form-section__header">
              <span className="ldas-form-section__title">카테고리 태그</span>
              <button
                type="button"
                className={`ldas-form-section__manage-categories-btn${
                  isCategoryPanelOpen
                    ? " ldas-form-section__manage-categories-btn--active"
                    : ""
                }`}
                onClick={handleToggleCategoryPanel}
                aria-expanded={isCategoryPanelOpen}
                aria-controls="ldas-create-category-aside"
                aria-label={
                  isCategoryPanelOpen
                    ? "카테고리 관리 패널 닫기"
                    : "카테고리 관리 패널 열기"
                }
              >
                카테고리 관리
              </button>
            </div>
            <div className="ldas-category-tags">
              {categories?.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`ldas-category-tag-btn${
                    isCategoryTagSelected(cat)
                      ? " ldas-category-tag-btn--selected"
                      : ""
                  }`}
                  onClick={() => handleTagToggle(cat)}
                >
                  {cat.name}
                </button>
              ))}
              <button
                type="button"
                className={`ldas-category-tag-add-btn${
                  isCategoryPanelOpen
                    ? " ldas-category-tag-add-btn--active"
                    : ""
                }`}
                onClick={handleToggleCategoryPanel}
                aria-expanded={isCategoryPanelOpen}
                aria-controls="ldas-create-category-aside"
                aria-label={
                  isCategoryPanelOpen
                    ? "카테고리 관리 패널 닫기"
                    : "카테고리 관리 패널 열기"
                }
              >
                <span aria-hidden className="ldas-category-tag-add-btn__plus">
                  +
                </span>
              </button>
            </div>
          </div>

          {/* 제품 이미지 */}
          <div className="ldas-form-section">
            <div className="ldas-form-section__header">
              <span className="ldas-form-section__title">제품 이미지</span>
            </div>
            <div className="ldas-form-section__image-wrap">
              <input
                ref={imageInputRef}
                type="file"
                accept={IMAGE_INPUT_ACCEPT}
                className="ldas-image-upload__input"
                aria-hidden
                tabIndex={-1}
                onChange={handleImageFileChange}
              />
              {imagePreviewSrc ? (
                <div className="ldas-image-upload ldas-image-upload--has-preview">
                  <div
                    className="ldas-image-upload__actions"
                    role="group"
                    aria-label="제품 이미지 작업"
                  >
                    <button
                      type="button"
                      className="ldas-btn ldas-btn--cancel ldas-image-upload__action-btn"
                      onClick={handleOpenImagePicker}
                      aria-label="다른 이미지로 변경"
                    >
                      이미지 변경
                    </button>
                    <button
                      type="button"
                      className="ldas-btn ldas-btn--cancel ldas-image-upload__action-btn"
                      onClick={handleRemoveImage}
                      aria-label="제품 이미지 삭제"
                    >
                      삭제
                    </button>
                  </div>
                  <div className="ldas-image-upload__preview-frame">
                    <img
                      src={imagePreviewSrc}
                      alt="제품 이미지 미리보기"
                      className="ldas-image-upload__preview"
                    />
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="ldas-image-upload"
                  onClick={handleOpenImagePicker}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenImagePicker();
                    }
                  }}
                  aria-label="클릭하여 제품 이미지 업로드, JPG 또는 PNG 최대 5MB"
                >
                  <PictureOutlined
                    className="ldas-image-upload__icon"
                    aria-hidden
                  />
                  <span className="ldas-image-upload__main">
                    클릭하여 이미지 업로드
                  </span>
                  <span className="ldas-image-upload__sub">
                    JPG, PNG · 최대 5MB
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 푸터 액션 */}
        <div className="ldas-create-form-card__footer">
          <button
            type="button"
            className="ldas-btn ldas-btn--cancel"
            onClick={onCancel}
          >
            취소
          </button>
          {mode === "edit" && typeof onDelete === "function" && (
            <button
              type="button"
              className="ldas-btn ldas-btn--danger"
              onClick={onDelete}
              aria-label="제품 삭제"
            >
              삭제
            </button>
          )}
          <button
            type="button"
            className="ldas-btn ldas-btn--primary"
            onClick={() => onSubmit(form)}
          >
            {mode === "edit" ? "수정 완료" : "저장"}
          </button>
        </div>
      </div>

      <aside
        id="ldas-create-category-aside"
        className="ldas-create-category-aside"
        aria-hidden={!isCategoryPanelOpen}
      >
        {isCategoryPanelOpen && (
          <LdasProductCategoryPanel
            categories={categories}
            onCategoriesInvalidated={onCategoriesInvalidated}
          />
        )}
      </aside>
    </div>
  );
};

export default LdasProductForm;
